import styled from 'styled-components';
import theme from '../../Theme';
import { MARKETING_CATEGORY_ALIGNED_PADDING_X } from '../../pages/LandingPage/styles/marketingLandingStyles';
import { MOBILE_BOTTOM_NAV_FOOTER_CLEARANCE, MOBILE_STICKY_CTA_FOOTER_CLEARANCE } from '../navigation/BottomNavigationStyles';

const footerSurface = theme.palette.background.paper;
const footerText = theme.palette.text.primary;
const footerMuted = theme.palette.text.secondary;
const footerDivider = theme.palette.border.subtle;

const footerBottomPadding = ({ $clearStickyCta, $clearBottomNav }) => {
  if ($clearStickyCta) return MOBILE_STICKY_CTA_FOOTER_CLEARANCE;
  if ($clearBottomNav) return MOBILE_BOTTOM_NAV_FOOTER_CLEARANCE;
  return '40px';
};

const footerBottomPaddingMobile = ({ $clearStickyCta, $clearBottomNav }) => {
  if ($clearStickyCta) return MOBILE_STICKY_CTA_FOOTER_CLEARANCE;
  if ($clearBottomNav) return MOBILE_BOTTOM_NAV_FOOTER_CLEARANCE;
  return 'calc(36px + env(safe-area-inset-bottom, 0px))';
};

/** Wider than the page column so the footer groups have room to sit in one row. */
export const FOOTER_MAX_WIDTH = 1680;
const FOOTER_GUTTER_PX = 24;
const FOOTER_WIDTH = `min(${FOOTER_MAX_WIDTH}px, calc(100% - ${FOOTER_GUTTER_PX * 2}px))`;
export const FOOTER_WIDTH_MOBILE = `calc(100% - ${FOOTER_GUTTER_PX * 2}px)`;

/** Marketplace list pages use `px: 2.5` on phones. Match that inset so the footer lines up with the page. */
const MARKETPLACE_PAGE_PADDING_X_MOBILE = theme.spacing(2.5);

const footerPaddingX = ({ $marketplace }) => (
  $marketplace ? MARKETPLACE_PAGE_PADDING_X_MOBILE : `${MARKETING_CATEGORY_ALIGNED_PADDING_X}px`
);

const footerColumnWidthMobile = ({ $marketplace }) => (
  $marketplace ? '100%' : FOOTER_WIDTH_MOBILE
);

export const FooterContainer = styled.footer`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: ${theme.typography.fontFamily};
  background: ${footerSurface};
  color: ${footerText};
  box-sizing: border-box;
  border-top: 1px solid ${footerDivider};

  ${({ $guestFrame, $signedInHome }) => ($guestFrame || $signedInHome) && `
    border-top-color: ${footerSurface};
  `}
`;

export const FooterContent = styled.div`
  position: relative;
  max-width: ${FOOTER_MAX_WIDTH}px;
  margin: 0 auto;
  padding-top: 52px;
  padding-right: ${footerPaddingX};
  padding-bottom: ${footerBottomPadding};
  padding-left: ${footerPaddingX};
  box-sizing: border-box;
  width: ${footerColumnWidthMobile};

  ${theme.breakpoints.up('sm')} {
    width: ${FOOTER_WIDTH};
  }

  @media (max-width: 768px) {
    padding-top: 40px;
    padding-bottom: ${footerBottomPaddingMobile};
  }
`;

export const TopSection = styled.div`
  display: flex;
  align-items: flex-start;
  padding-bottom: 32px;

  @media (max-width: 768px) {
    padding-bottom: 28px;
  }
`;

export const LinkGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 32px 48px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;

  @media (max-width: 768px) {
    justify-content: flex-start;
    gap: 28px 40px;
  }
`;

export const NavColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

export const ColumnTitle = styled.p`
  font-size: var(--font-caption);
  font-weight: 700;
  color: ${footerMuted};
  margin: 0;
  font-family: ${theme.typography.fontFamily};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const BaseLink = styled.a`
  color: ${footerMuted};
  text-decoration: none;
  transition: color 0.15s ease;
  font-family: ${theme.typography.fontFamily};
  font-weight: 400;

  &:hover {
    color: ${footerText};
  }
`;

export const NavLink = styled(BaseLink)`
  font-size: var(--font-body-sm);
  line-height: 1.45;
`;

/** Same visual weight as NavLink, for non-navigation footer actions (e.g. cookie settings). */
export const NavActionButton = styled.button`
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  text-align: left;
  cursor: pointer;
  color: ${footerMuted};
  text-decoration: none;
  transition: color 0.15s ease;
  font-family: ${theme.typography.fontFamily};
  font-weight: 400;
  font-size: var(--font-body-sm);
  line-height: 1.45;

  &:hover {
    color: ${footerText};
  }
`;

export const AskAiSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

export const AskAiTitle = styled.p`
  font-size: var(--font-micro);
  font-weight: 700;
  color: ${footerText};
  margin: 0;
  font-family: ${theme.typography.fontFamily};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1.3;
  text-align: left;
`;

export const AskAiLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 6px;
  align-items: center;
`;

export const AskAiButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${theme.palette.accent.main};
  color: ${theme.palette.primary.contrastText};
  text-decoration: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
  opacity: 0.95;

  &:hover {
    opacity: 1;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${theme.palette.accent.main};
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

export const FooterEnd = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding-top: 20px;

  @media (max-width: 768px) {
    gap: 16px;
    padding-top: 16px;
  }
`;

export const CopyrightGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px 20px;
`;

export const Copyright = styled.p`
  font-size: var(--font-body-sm);
  color: ${footerMuted};
  margin: 0;
  font-family: ${theme.typography.fontFamily};
  font-weight: 400;
`;

export const BackedBy = styled(BaseLink)`
  font-size: var(--font-body-sm);
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const SocialButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: ${(props) => props.$brandColor || footerText};
  transition: opacity 0.2s ease, transform 0.2s ease;
  cursor: pointer;
  text-decoration: none;
  opacity: 0.9;

  &:hover {
    opacity: 1;
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid ${theme.palette.accent.main};
    outline-offset: 3px;
    border-radius: 4px;
  }

  svg,
  img {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  img {
    object-fit: contain;
    display: block;
  }

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;
